// LeadDocumentsPanel — the Documents surface from the spec.
//
// Documents live per-CLIENT (client_documents table, mig 137), so
// a lead needs to be converted before uploads route here. Two
// states:
//
//   Not yet converted:  honest hint — "Convert the lead first,
//                       then you can attach docs like DL, income
//                       verification, insurance cards, etc."
//   Converted:          list of docs for the linked client_id
//                       with an Upload button that uses the
//                       existing upload-url flow.

import React from 'react';
import { readPersistedJwtDirect } from './authedFetch';
import { supabase } from '../lib/supabase';

async function bearer() {
  const token = readPersistedJwtDirect();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function fmtBytes(n) {
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)}KB`;
  return `${(n / 1024 / 1024).toFixed(1)}MB`;
}

const DOC_ICON = {
  drivers_license:     '🪪',
  insurance_card:      '💳',
  income_verification: '💰',
  commission_statement:'📊',
  soa:                 '✍️',
  quote_pdf:           '📄',
  application:         '📋',
  recorded_call:       '🎙️',
  voice_memo:          '🎙️',
  other:               '📎',
};

export default function LeadDocumentsPanel({ lead }) {
  const clientId = lead?.convertedClientId || lead?.client_id;
  const [state, setState] = React.useState({ loading: false, rows: [], error: null });
  const [uploading, setUploading] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!clientId) return;
    setState(s => ({ ...s, loading: true }));
    try {
      const r = await fetch(`/api/documents/list?client_id=${encodeURIComponent(clientId)}`, {
        headers: await bearer(),
      });
      const j = await r.json();
      if (r.ok && j.rows) setState({ loading: false, rows: j.rows, error: null });
      else setState({ loading: false, rows: [], error: j.error || `HTTP ${r.status}` });
    } catch (err) {
      setState({ loading: false, rows: [], error: err.message });
    }
  }, [clientId]);

  React.useEffect(() => { load(); }, [load]);

  if (!clientId) {
    return (
      <div style={{
        background: '#f8fafc', border: '1px dashed #cbd5e1',
        borderRadius: 10, padding: 12,
      }}>
        <div style={{ fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#475569', fontWeight: 800, marginBottom: 6 }}>
          Documents
        </div>
        <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.55 }}>
          Convert this lead to a client (button above) to attach documents.
          DL, insurance cards, income verification, commission statements,
          quotes, SOAs, application PDFs — all searchable via OCR once loaded.
        </div>
      </div>
    );
  }

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      // Existing flow: get a signed upload URL, PUT the file, then
      // register the doc row.
      const urlRes = await fetch('/api/documents/upload-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await bearer()) },
        body: JSON.stringify({ client_id: clientId, filename: file.name, mime_type: file.type }),
      });
      const urlJ = await urlRes.json();
      if (!urlRes.ok || !urlJ.signed_url) throw new Error(urlJ.error || `upload-url ${urlRes.status}`);
      const putRes = await fetch(urlJ.signed_url, { method: 'PUT', body: file });
      if (!putRes.ok) throw new Error(`storage put ${putRes.status}`);
      await fetch('/api/documents/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await bearer()) },
        body: JSON.stringify({
          client_id:      clientId,
          filename:       file.name,
          storage_path:   urlJ.storage_path,
          mime_type:      file.type,
          size_bytes:     file.size,
        }),
      });
      await load();
    } catch (err) {
      alert(`Upload failed: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div style={{
      background: '#f0f9ff',
      border: '1px solid #bae6fd',
      borderRadius: 10, padding: 12,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
        <div style={{ fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#0369a1', fontWeight: 800 }}>
          Documents
        </div>
        <div style={{ fontSize: 11, color: '#075985' }}>
          {state.rows.length} on file
        </div>
        <label style={{
          marginLeft: 'auto',
          background: '#0284c7', color: '#ffffff',
          padding: '4px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700,
          cursor: uploading ? 'wait' : 'pointer',
          opacity: uploading ? 0.7 : 1,
        }}>
          {uploading ? 'Uploading…' : '+ Attach'}
          <input
            type="file"
            style={{ display: 'none' }}
            disabled={uploading}
            onChange={(e) => upload(e.target.files?.[0])}
          />
        </label>
      </div>

      {state.error && (
        <div style={{ fontSize: 11, color: '#dc2626', padding: 8, background: '#fef2f2', borderRadius: 6 }}>
          {state.error}
        </div>
      )}

      {state.rows.length === 0 && !state.error && (
        <div style={{ fontSize: 11, color: '#64748b', padding: 10, textAlign: 'center', fontStyle: 'italic' }}>
          No docs yet — attach a DL / income verification / carrier application to get started.
        </div>
      )}

      {state.rows.length > 0 && (
        <div style={{ display: 'grid', gap: 4 }}>
          {state.rows.map(r => (
            <div key={r.id} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: '#ffffff', border: '1px solid #bae6fd',
              borderRadius: 6, padding: '6px 10px',
            }}>
              <div style={{ fontSize: 18 }}>{DOC_ICON[r.doc_type] || DOC_ICON.other}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: '#0f172a', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.filename}
                </div>
                <div style={{ fontSize: 10, color: '#64748b', marginTop: 1 }}>
                  {r.doc_type || 'other'} · {fmtBytes(r.size_bytes)} · {r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}
                </div>
              </div>
              {r.ocr_status === 'complete' && (
                <div style={{ fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#059669', fontWeight: 800 }}>
                  OCR
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
